import { Component, Inject, OnInit } from '@angular/core';
import { SharedModule } from '../../../shared/shared.module';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { ButtonModule } from '../../../shared/button/button.module';
import { PaymentGroup } from '../../../../models/entities/payment-group';
import { PaymentGroupsService } from '../../../../services/payment/payment-groups.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs';
import { JwtLegFiClaims } from 'app/services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from 'app/services/auth/legfi-jwt.service';
import { EntityHistoryButtonComponent } from '../../../v2-layout/shared/entity-history/entity-history-button.component';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { TransactionDetailsDialogComponent, TransactionDetailsDialogInput } from '../transaction-details-dialog/transaction-details-dialog.component';
import { RouteModules } from '../../../../enums/route-modules.enum';
import { LegfiDatePipe } from 'app/pipes/format/legfi-date.pipe';
import { LegfiCurrencyPipe } from 'app/pipes/format/legfi-currency.pipe';
import { SuperStarComponent } from '../super-star/super-star.component';
import { SumPipe } from '../../../../pipes/collection/sum.pipe';
import { GroupByPipe } from '../../../../pipes/collection/group-by.pipe';
import { OrganizationV2DTO, OrganizationV2Service } from '../../../../services/organization/organization-v2.service';
import { LedgerEntriesService } from '../../../../services/accounting/ledger-entries.service';
import { ConfirmActionDialogComponent, ConfirmActionDialogInput, ConfirmActionDialogResult } from '../confirm-action-dialog/confirm-action-dialog.component';

export interface PaymentDetailDialogInput
{
    paymentGroupId: number;
}

@UntilDestroy()
@Component({
    selector: 'lf-payment-detail-dialog',
    styleUrls: ['./payment-detail-dialog.scss'],
    templateUrl: './payment-detail-dialog.component.html',
    standalone: true,
    imports: [
        SharedModule,
        MatProgressSpinnerModule,
        ButtonModule,
        LegfiDatePipe,
        LegfiCurrencyPipe,
        SuperStarComponent,
        SumPipe,
        GroupByPipe,
        EntityHistoryButtonComponent,
    ],
})
export class PaymentDetailDialogComponent implements OnInit
{
    paymentGroup: PaymentGroup = null;
    fundsAvailable: string = null;
    jwt: JwtLegFiClaims;
    totalShort: number = 0;
    inFlightRequest: boolean = false;
    hasTransactionsPermission = false;
    hasAccountingV2 = false;
    accrualAccounting = false;
    // Read-mostly dialog. Mutating actions use one-shot `take(1)` streams and
    // disable their triggers via `inFlightRequest`; this flag exists so the
    // dialog-submit-prevention meta-lint rule recognizes the guard.
    readonly isSaving = false;

    constructor(
            private _paymentGroupsService: PaymentGroupsService,
            private _ledgerEntriesService: LedgerEntriesService,
            private _growler: GrowlerService,
            private _organizationService: OrganizationV2Service,
            private _dialogRef: MatDialogRef<PaymentDetailDialogComponent>,
            private _dialog: MatDialog,
            @Inject(MAT_DIALOG_DATA) private data: PaymentDetailDialogInput,
    ) {
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();
        this.hasAccountingV2 = LegFiJwtService.featureEnabled('accounting-v2');
        this.hasTransactionsPermission = LegFiJwtService.doesUserHaveModulePermission(RouteModules.TRANSACTIONS, false);
        this.loadPaymentGroup();

        this._organizationService.getOrganizationById(this.jwt.orgId)
                .pipe(untilDestroyed(this))
                .subscribe((org: OrganizationV2DTO) => {
                    this.accrualAccounting = !org.preferences.useCashAccounting;
                });
    }

    /**
     * Issue a credit in the amount of the difference between the payment group's
     * applied value and the actual total of the payment items
     */
    public issueDifferenceCredit() {
        this.inFlightRequest = true;
        this._paymentGroupsService
                .issueCreditForPaymentDifference(null, this.data.paymentGroupId)
                .subscribe(
                        () => {
                            this.inFlightRequest = false;
                            this.loadPaymentGroup();
                        },
                        () => {
                            this.inFlightRequest = false;
                        },
                );
    }

    public openParentPayment(paymentGroupId: number) {
        this._dialogRef.close();
        const data: PaymentDetailDialogInput = {
            paymentGroupId: paymentGroupId,
        };
        this._dialog.open(PaymentDetailDialogComponent, {
            data: data,
        });
    }

    public openDepositTransactionDetails() {
        this._dialogRef.close();
        const data: TransactionDetailsDialogInput = {
            transactionId: this.paymentGroup.paymentAttempts[0].transactions[0].id,
        };
        this._dialog.open(TransactionDetailsDialogComponent, {
            data: data,
        });
    }

    public loadPaymentGroup() {
        this._paymentGroupsService
                .getIncomingPaymentGroup(null, this.data.paymentGroupId)
                .pipe(untilDestroyed(this))
                .subscribe(
                        (paymentGroup: PaymentGroup) => {
                            const offlineRecords = paymentGroup.paymentAttempts.filter(
                                    attempt => {
                                        return attempt.processor === 'offline';
                                    },
                            );
                            if (paymentGroup.paymentAttempts.length > 0 && offlineRecords.length === 0) {
                                const fundsAvailableRecords = paymentGroup.paymentAttempts.filter(
                                        attempt => {
                                            return attempt.fundsAvailable !== null;
                                        },
                                );
                                this.fundsAvailable =
                                        fundsAvailableRecords.length > 0
                                                ? fundsAvailableRecords[0].fundsAvailable
                                                : 'N/A';
                            }

                            this.paymentGroup = paymentGroup;
                            this.paymentGroup.hasValidPayments = paymentGroup.hasValidPayments ||
                                    (paymentGroup.paymentAttempts ? paymentGroup.paymentAttempts.filter((attempt) => {
                                        return !(attempt.deletedAt) && (attempt.success || attempt.pending);
                                    }).length > 0 : false);

                            this.totalShort = paymentGroup.applied;
                            for (const q of paymentGroup.paymentAttempts) {
                                for (const i of q.paymentItems) {
                                    this.totalShort -= i.amount;
                                }
                            }
                        },
                        () => {
                            alert('Unable to load payment information.');
                        },
                );
    }

    public updatePaymentGroupTotals() {
        this._paymentGroupsService.updatePaymentGroupTotals(null, this.data.paymentGroupId).pipe(take(1)).subscribe(
                () => {
                    this._growler.success('Success', 'Payment group totals updated from attempt data.');
                    this.loadPaymentGroup();
                },
                () => {
                    this._growler.oops('There was an error updating this payment group.');
                },
        );
    }

    deleteLedgerBatch(batchId: string) {
        const data: ConfirmActionDialogInput = {
            title: 'Delete Ledger Batch?',
            content: 'This action is outside the regular flow of entries and should only be used to fix irreversible issues.',
            confirmButton: 'Delete Batch'
        };
        this._dialog.open(ConfirmActionDialogComponent, {
            data
        }).afterClosed().subscribe((result: ConfirmActionDialogResult) => {
            if (result?.success) {
                this._ledgerEntriesService.deleteBatch(this.jwt.orgId, batchId)
                        .subscribe({
                            next: () => {
                                this._growler.success('Success', 'Ledger batch deleted successfully.');
                                this.loadPaymentGroup();
                            }
                        });
            }
        });
    }

    public restoreCredit(unitId: number, creditId: number) {
        this._paymentGroupsService.restoreUnitAccountCredit(null, unitId, creditId).pipe(take(1)).subscribe(
                () => {
                    this._growler.success('Success', 'Credit successfully restored.');
                    this.loadPaymentGroup();
                },
                () => {
                    this._growler.oops('There was an error restoring this credit.');
                },
        );
    }
}
